import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { auth } from './firebase';
import axios from 'axios';
import Auth from './components/Auth';
import Dashboard from './components/Dashboard';
import AdminDashboard from './components/AdminDashboard';
import Profile from './components/Profile';
import ManageApplications from './components/ManageApplications';
import ApplyToTutor from './components/ApplyToTutor'; 
import MyApplications from './components/MyApplications';
import ManageCourses from './components/ManageCourses';
import RequestSession from './components/RequestSession';
import OnlineSession from './components/OnlineSession'; 
import FaceToFaceSession from './components/FaceToFaceSession'; 
import FaceToFaceTracking from './components/FaceToFaceTracking';
import VideoCallRoom from './components/VideoCallRoom';
import WaitingRoom from './components/WaitingRoom';
import Wallet from './components/Wallet';
import ManagePayments from './components/ManagePayments';
import Notifications from './components/Notifications';
import Terms from './components/Terms';
import LandingPage from "./components/LandingPage";
import AboutTeachMeGo from "./components/AboutTeachMeGo";
import Connect from './components/Connect';
import Home from './components/Home';
import Chats from './components/Chats';
import { ChatProvider } from './components/ChatContext';

import './App.css';

function App() {
  const [user, setUser] = useState(null);
  const [admin, setAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      setUser(user);
      if (user) {
        try {
          const token = await user.getIdToken();
          const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/user/${user.uid}/data`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          setAdmin(res.data.admin === true);
        } catch (err) {
          console.error("Error fetching user admin status:", err);
        }
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  if (loading) return <div className="app-loading">Loading...</div>;

  return (
    <BrowserRouter>
      <Routes>
        {/* Public routes - NO ChatProvider needed */}
        <Route path="/terms" element={<Terms />} />
        <Route path="/" element={<LandingPage />} />
        <Route path="/about" element={<AboutTeachMeGo />} />
        <Route path="/auth" element={!user ? <Auth /> : <Navigate to={admin ? "/admin" : "/dashboard"} />} />

        {/* Protected routes that NEED chat functionality */}
        <Route path="/dashboard" element={
          user && !admin ? (
            <ChatProvider> {/* ChatProvider only for dashboard routes */}
              <Dashboard />
            </ChatProvider>
          ) : (
            <Navigate to="/auth" />
          )
        }>
          <Route index element={<Home />} />
          <Route path="profile" element={<Profile />} />
          <Route path="apply" element={<ApplyToTutor />} />
          <Route path="wallet" element={<Wallet />} />
          <Route path="connect" element={<Connect />} />
          <Route path="chats/*" element={<Chats />} />
          <Route path="applications" element={<MyApplications />} />
          <Route path="notifications" element={<Notifications />} />
          <Route path="tracking/:sessionId" element={<FaceToFaceTracking />} />
          <Route path="request" element={<RequestSession />}>
            <Route path="online/:courseId/:duration" element={<OnlineSession />} />
            <Route path="face-to-face/:courseId/:duration" element={<FaceToFaceSession />} />
          </Route>
          <Route path="video/:sessionId" element={<VideoCallRoom />} />
          <Route path="waiting/:sessionId" element={<WaitingRoom />} />
        </Route>

        {/* Admin routes - might need chat for admin-user communication */}
        <Route path="/admin" element={
          user && admin ? (
            <ChatProvider> {/* ChatProvider for admin routes too */}
              <AdminDashboard />
            </ChatProvider>
          ) : (
            <Navigate to="/auth" />
          )
        }>
          <Route index element={<div>Welcome to the admin dashboard!</div>} />
          <Route path="applications" element={<ManageApplications />} />
          <Route path="courses" element={<ManageCourses />} />
          <Route path="payments" element={<ManagePayments />} />
        </Route>

        {/* Redirect old video URL */}
        <Route path="/video/:sessionId" element={
          user ? <Navigate to={`/dashboard/video/${window.location.pathname.split('/')[2]}`} /> : <Navigate to="/auth" />
        } />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
